/**
 * badgesUtils.ts - Status Badge Logic and Display Utilities
 *
 * This utility module handles the logic for displaying status badges on fundraisers,
 * including expiration warnings and pending start notifications.
 *
 * Key Functions:
 * - getExpirationBadgeText: Generates time-sensitive expiration text
 * - shouldShowExpirationBadge: Determines when to show expiration warnings
 * - getPendingBadgeText: Generates pending start date text
 * - shouldShowPendingBadge: Determines when to show pending notifications
 *
 * Features:
 * - Time-sensitive badge text (hours, minutes, days)
 * - Smart badge visibility logic based on dates and status
 * - Pending vs expiration badge priority handling
 * - Real-time countdown calculations
 *
 * Dependencies: None (pure JavaScript Date handling)
 */

import { FundRaiserStatus } from "../enums";

// Shared utility for badge logic (formerly expirationUtils.ts)
export const getExpirationBadgeText = (
  endDate: Date | string | null | number
): string => {
  if (!endDate) return "";

  const endDateTime = new Date(endDate);
  if (isNaN(endDateTime.getTime())) return "";

  const now = new Date();

  // Get start of today and tomorrow for comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
  const endDateDay = new Date(
    endDateTime.getFullYear(),
    endDateTime.getMonth(),
    endDateTime.getDate()
  );

  // Check if expiration is today
  if (endDateDay.getTime() === today.getTime()) {
    const timeToExpiration = endDateTime.getTime() - now.getTime();
    const hoursRemaining = Math.floor(timeToExpiration / (1000 * 60 * 60));
    const minutesRemaining = Math.floor(
      (timeToExpiration % (1000 * 60 * 60)) / (1000 * 60)
    );

    // More than 60 minutes: show hours
    if (minutesRemaining > 60) {
      return `EXPIRES IN ${hoursRemaining} HRS`;
    }
    // 1-60 minutes: show minutes
    else if (minutesRemaining >= 1) {
      return `EXPIRES IN ${minutesRemaining} MINS`;
    }
    // Less than 1 minute: show "1 MINUTE"
    else {
      return `EXPIRES IN 1 MIN`;
    }
  }

  // Check if expiration is tomorrow
  if (endDateDay.getTime() === tomorrow.getTime()) {
    return "EXPIRES IN 1 DAY";
  }

  // For other days, calculate days remaining
  const timeToExpiration = endDateTime.getTime() - now.getTime();
  const daysToExpiration = Math.ceil(timeToExpiration / (1000 * 60 * 60 * 24));

  if (daysToExpiration > 1) {
    return `EXPIRES IN ${daysToExpiration} DAYS`;
  }

  return `EXPIRES IN ${daysToExpiration} DAYS`;
};

export const shouldShowExpirationBadge = (
  endDate: Date | string | null,
  status: keyof typeof FundRaiserStatus
): boolean => {
  if (!endDate || status !== FundRaiserStatus.Active) return false;

  const endDateTime = new Date(endDate);
  if (isNaN(endDateTime.getTime())) return false;

  const now = new Date();
  const timeToExpiration = endDateTime.getTime() - now.getTime();
  const daysToExpiration = timeToExpiration / (1000 * 60 * 60 * 24);

  return daysToExpiration > 0 && daysToExpiration <= 7;
};

export const getPendingBadgeText = (
  startDate: Date | string | null
): string => {
  if (!startDate) return "";

  const startDateTime = new Date(startDate);
  if (isNaN(startDateTime.getTime())) return "";

  const now = new Date();

  // Get start of today and the start date for comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startDateDay = new Date(
    startDateTime.getFullYear(),
    startDateTime.getMonth(),
    startDateTime.getDate()
  );

  const timeToStart = startDateDay.getTime() - today.getTime();
  const daysToStart = Math.ceil(timeToStart / (1000 * 60 * 60 * 24));

  if (daysToStart === 1) {
    return "STARTS IN 1 DAY";
  }

  return `STARTS IN ${daysToStart} DAYS`;
};

export const shouldShowPendingBadge = (
  startDate: Date | string | null,
  status: string
): boolean => {
  if (!startDate || (status !== "active" && status !== "draft")) return false;

  const startDateTime = new Date(startDate);
  if (isNaN(startDateTime.getTime())) return false;

  const now = new Date();
  const startDateOnly = new Date(
    startDateTime.getFullYear(),
    startDateTime.getMonth(),
    startDateTime.getDate()
  );
  const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  return startDateOnly > todayOnly;
};
